import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { useAuthHeaders } from "../auth/apiClient";
import type { CustomerSummary, NextBestAction, QuoteSummary, TimelineEvent } from "./useCustomer360";

export interface ClientGhost {
  ghosted: boolean;
  ghost_reason: string | null;
  ghosted_by: string | null;
  ghosted_at: string | null;
}

export interface Quote360 {
  quote: QuoteSummary;
  customer: CustomerSummary;
  ghost?: ClientGhost;
  timeline: TimelineEvent[];
  next_best_action: NextBestAction;
  action_assistant?: QuoteActionAssistant;
  followup_reminder?: QuoteFollowupReminder | null;
  followup_reminder_suggestion?: QuoteFollowupReminder | null;
  tenant?: {
    timezone: string;
    timezone_label: string;
  };
  generated_at: string;
}

export interface QuoteFollowupReminder {
  id?: number;
  quote_id: string;
  contact_id: number;
  source_email_response_id?: number | null;
  reminder_date: string;
  due_at_utc?: string | null;
  reason: string;
  comment?: string | null;
  suggested_action: string;
  status: "pending" | "suggested" | "done" | "dismissed" | "cancelled";
  source?: string | null;
  created_by?: string | null;
  display_mode?: "actionable" | "info";
  info_text?: string | null;
  customer_message_angle?: string;
  draft_seed?: string;
}

export type QuoteActionType = "personalized_reply" | "wisetack" | "schedule_opening" | "before_after_gallery" | "helpful_followup" | "manual_reply";
export type QuoteActionChannel = "email" | "sms";

export interface QuoteActionAssistant {
  status: "ready" | "blocked";
  recommended_action: QuoteActionType | null;
  reason: string;
  guard: {
    blocked: boolean;
    warning?: boolean;
    severity?: "warning" | "blocked";
    code?: string;
    reason: string;
    recent?: {
      channel: string;
      template_id: string;
      sent_at: string;
      quote_id: string | null;
    };
  };
  available_actions: Array<{ type: QuoteActionType; label: string }>;
  customer_name?: string;
  quote_id?: string;
  amount?: number;
}

export interface QuoteActionPreview {
  ok: true;
  actionType: QuoteActionType;
  channel: QuoteActionChannel;
  quote_id: string;
  customer_name: string;
  subject: string;
  body: string;
  intent?: string;
  generatedBy?: string;
  rationale?: string;
  guard: QuoteActionAssistant["guard"];
}

interface RiskQueueCache {
  items: Array<{ quote_id: string }>;
}

export function removeQuoteFromRiskQueue(
  current: RiskQueueCache | undefined,
  quoteId: string | null,
): RiskQueueCache | undefined {
  if (!current || !quoteId) return current;
  return {
    ...current,
    items: current.items.filter((item) => String(item.quote_id) !== String(quoteId)),
  };
}

function clearCurrentTodayAction(queryClient: QueryClient, quoteId: string | null) {
  queryClient.setQueryData<RiskQueueCache>(
    ["control-tower", "quotes", "risk-queue"],
    (current) => removeQuoteFromRiskQueue(current, quoteId),
  );
}

export function useSetClientGhost(quoteId: string | null) {
  const headers = useAuthHeaders(true);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { ghosted: boolean; reason?: string }) => {
      const res = await fetch(`/api/cockpit/quotes/${quoteId}/ghost`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.reason || body.error || "Ghost update failed");
      return body as { ok: true; contact_id: number; ghost: ClientGhost };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["quote-360", quoteId] });
      queryClient.invalidateQueries({ queryKey: ["customer-360", result.contact_id] });
    },
  });
}

export function useQuote360(quoteId: string | null) {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ["quote-360", quoteId],
    queryFn: async () => {
      const res = await fetch(`/api/cockpit/quotes/${quoteId}/360`, {
        headers,
      });
      if (!res.ok) throw new Error("Quote 360 failed");
      return res.json() as Promise<Quote360>;
    },
    enabled: !!quoteId,
    staleTime: 5 * 60_000,
  });
}

export function useQuoteActionPreview(quoteId: string | null) {
  const headers = useAuthHeaders(true);
  return useMutation({
    mutationFn: async (payload: { actionType: QuoteActionType; channel?: QuoteActionChannel; promoAmount?: string }) => {
      const res = await fetch(`/api/cockpit/quotes/${quoteId}/actions/preview`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.reason || body.error || "Quote action preview failed");
      return body as QuoteActionPreview;
    },
  });
}

export function useSendQuoteAction(quoteId: string | null) {
  const headers = useAuthHeaders(true);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { actionType: QuoteActionType; channel?: QuoteActionChannel; subject: string; body: string; force?: boolean }) => {
      const forceSuffix = payload.force ? "?force=true" : "";
      const res = await fetch(`/api/cockpit/quotes/${quoteId}/actions/send${forceSuffix}`, {
        method: "POST",
        headers,
        body: JSON.stringify({ ...payload, confirmToken: "YES-QUOTE-ACTION-SEND" }),
      });
      const body = await res.json();
      if (!res.ok) {
        const err = new Error(body.reason || body.detail || body.error || "Quote action send failed");
        (err as Error & { payload?: unknown }).payload = body;
        throw err;
      }
      return body as { ok: true; channel: QuoteActionChannel; sentAt: string; messageId?: string | null };
    },
    onSuccess: () => {
      clearCurrentTodayAction(queryClient, quoteId);
      queryClient.invalidateQueries({ queryKey: ["quote-360", quoteId] });
      queryClient.invalidateQueries({ queryKey: ["control-tower", "quotes", "risk-queue"] });
      queryClient.invalidateQueries({ queryKey: ["control-tower", "activity", "recent"] });
    },
  });
}

export type ConversationDisposition =
  | "HANDLED"
  | "KEEP_FOLLOWUP"
  | "CLOSED"
  | "CONVERTED"
  | "REMINDER_SET"
  | "handled"
  | "client_called"
  | "call_received"
  | "closed"
  | "antonio_sms_sent"
  | "antonio_email_sent";

const TODAY_CLEARING_DISPOSITIONS = new Set<ConversationDisposition>([
  "HANDLED",
  "CLOSED",
  "CONVERTED",
]);

export function shouldClearTodayAfterConversationMark(
  disposition: ConversationDisposition,
): boolean {
  return TODAY_CLEARING_DISPOSITIONS.has(disposition);
}

export function useMarkQuoteConversation(quoteId: string | null) {
  const headers = useAuthHeaders(true);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      disposition: ConversationDisposition;
      note?: string;
      reason_code?: string;
      comment?: string;
    }) => {
      const res = await fetch(`/api/cockpit/quotes/${quoteId}/conversation/mark`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.reason || body.error || "Conversation mark failed");
      return body as {
        ok: true;
        quote_id: string;
        disposition: ConversationDisposition;
        disposition_state: ConversationDisposition;
        legacy_note_disposition: ConversationDisposition;
        note: string;
        note_id: number;
        reason_code: string | null;
      };
    },
    onSuccess: (result) => {
      if (shouldClearTodayAfterConversationMark(result.disposition_state)) {
        clearCurrentTodayAction(queryClient, quoteId);
      }
      queryClient.invalidateQueries({ queryKey: ["quote-360", quoteId] });
      queryClient.invalidateQueries({ queryKey: ["control-tower", "quotes", "risk-queue"] });
      queryClient.invalidateQueries({ queryKey: ["control-tower", "activity", "recent"] });
    },
  });
}

export function useUndoQuoteConversation(quoteId: string | null) {
  const headers = useAuthHeaders(true);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { noteId: number }) => {
      const res = await fetch(`/api/cockpit/quotes/${quoteId}/conversation/undo`, {
        method: "POST",
        headers,
        body: JSON.stringify({ note_id: payload.noteId }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.reason || body.error || "Conversation undo failed");
      return body as {
        ok: true;
        quote_id: string;
        undone_note_id: number;
        disposition: ConversationDisposition;
        disposition_state?: ConversationDisposition | null;
        disposition_state_undone?: number;
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quote-360", quoteId] });
      queryClient.invalidateQueries({ queryKey: ["control-tower", "quotes", "risk-queue"] });
      queryClient.invalidateQueries({ queryKey: ["control-tower", "activity", "recent"] });
    },
  });
}

export function useCreateQuoteReminder(quoteId: string | null) {
  const headers = useAuthHeaders(true);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { reminder_date?: string; reason?: string; source_email_response_id?: number | null }) => {
      const res = await fetch(`/api/cockpit/quotes/${quoteId}/reminders`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.reason || body.error || "Reminder create failed");
      return body as { ok: true; quote_id: string; reminder_date: string; reason: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quote-360", quoteId] });
      queryClient.invalidateQueries({ queryKey: ["control-tower", "home", "alerts"] });
    },
  });
}

export function useCreateManualQuoteReminder(quoteId: string | null) {
  const headers = useAuthHeaders(true);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { date: string; time: string; comment: string }) => {
      const res = await fetch(`/api/cockpit/quotes/${quoteId}/reminders/manual`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.reason || body.error || "Manual reminder create failed");
      return body as {
        ok: true;
        quote_id: string;
        reminder_id: number;
        reminder_date: string;
        due_at_utc: string;
        time_zone: string;
        time_zone_label: string;
        comment: string;
        created_by: string;
        source: "quote360_manual";
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quote-360", quoteId] });
      queryClient.invalidateQueries({ queryKey: ["control-tower", "quotes", "risk-queue"] });
      queryClient.invalidateQueries({ queryKey: ["control-tower", "home", "alerts"] });
    },
  });
}

export function useCancelManualQuoteReminder(quoteId: string | null) {
  const headers = useAuthHeaders(true);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { reminderId: number }) => {
      const res = await fetch(`/api/cockpit/quotes/${quoteId}/reminders/manual/${payload.reminderId}/cancel`, {
        method: "POST",
        headers,
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.reason || body.error || "Manual reminder cancel failed");
      return body as { ok: boolean; quote_id: string; reminder_id: number; status: "cancelled" | "unchanged" };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quote-360", quoteId] });
      queryClient.invalidateQueries({ queryKey: ["control-tower", "quotes", "risk-queue"] });
      queryClient.invalidateQueries({ queryKey: ["control-tower", "home", "alerts"] });
    },
  });
}
